import Component from '@glimmer/component';
import { service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { htmlSafe } from '@ember/template';
import { concat } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { t } from 'ember-intl';
import Card from 'tio-ui/components/card';
import Chip from 'tio-ui/components/chip';
import type { ChipIntent } from 'tio-ui/components/chip';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import { PhBank, PhCalendar, PhTrendDown } from 'ember-phosphor-icons';
import { formatMonthYear } from 'tio-ui/format';
import type { LoanAccount } from 'tio-employee/components/reimagined/types';

interface LoanAccountCardSignature {
  Args: {
    /** One linked account, with its loans rolled up the way the summary shows them. */
    loan: LoanAccount;
  };
  Element: HTMLDivElement;
}

const RING_RADIUS = 40;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const TYPE_INTENT: Record<LoanAccount['accountType'], ChipIntent> = {
  federal: 'primary',
  private: 'warning',
  refinanced: 'success',
};

/**
 * A linked loan account as a progress card: servicer, loan count, account
 * type, whether it receives employer contributions, the balance with a
 * paid-off ring, the monthly payment and rate, the estimated payoff month,
 * and a link to the account's detail page.
 */
export default class LoanAccountCard extends Component<LoanAccountCardSignature> {
  @service declare intl: IntlService;

  /** A USD amount in the active locale; `digits` fixes the fraction digits when given. */
  money = (value: number | undefined | null, digits?: number) =>
    this.intl.formatNumber(value || 0, {
      style: 'currency',
      currency: 'USD',
      ...(digits === undefined
        ? {}
        : { minimumFractionDigits: digits, maximumFractionDigits: digits }),
    });

  get progressPercent() {
    const { originalBalance, balance } = this.args.loan;
    if (originalBalance <= 0) return 0;
    return Math.max(
      0,
      Math.min(100, Math.round(((originalBalance - balance) / originalBalance) * 100))
    );
  }

  get ringStyle() {
    const offset = RING_CIRCUMFERENCE - (this.progressPercent / 100) * RING_CIRCUMFERENCE;
    return htmlSafe(`stroke-dasharray: ${RING_CIRCUMFERENCE}; stroke-dashoffset: ${offset}`);
  }

  get typeIntent() {
    return TYPE_INTENT[this.args.loan.accountType];
  }

  get payoffMonth() {
    return formatMonthYear(this.args.loan.estimatedPayoff);
  }

  <template>
    <Card @padding="md" @radius="card" data-test-loan-account-card ...attributes>
      <div class="tio-row-start-between mb-4">
        <div class="tio-row">
          <PhBank @size={{24}} class="tio-fg-brand shrink-0" />
          <div>
            <Heading @level={{3}}>{{@loan.servicer}}</Heading>
            <Text @variant="muted" @size="sm">
              {{t "accounts.loan_summary.card.loan_count" count=@loan.loanCount}}
            </Text>
          </div>
        </div>
        <div class="tio-row-tight">
          <Chip @intent={{this.typeIntent}} @size="sm">
            {{t (concat "accounts.loan_summary.card.type." @loan.accountType)}}
          </Chip>
          {{#if @loan.isReceivingEmployerHelp}}
            <Chip @intent="success" @size="sm" data-test-loan-account-card-employer-help>
              {{t "accounts.loan_summary.card.employer_help"}}
            </Chip>
          {{/if}}
        </div>
      </div>

      <div class="tio-row-extra-loose mb-4">
        <div class="relative w-24 h-24 shrink-0">
          <svg
            class="w-full h-full transform -rotate-90"
            viewBox="0 0 100 100"
            role="img"
            aria-label={{t "accounts.loan_summary.card.paid_off" percent=this.progressPercent}}
            data-test-loan-account-card-ring
          >
            <circle
              cx="50"
              cy="50"
              r="40"
              fill="none"
              stroke="var(--border-subtle)"
              stroke-width="8"
            />
            <circle
              cx="50"
              cy="50"
              r="40"
              fill="none"
              class="tio-progress-ring--brand"
              stroke-width="8"
              stroke-linecap="round"
              style={{this.ringStyle}}
            />
          </svg>
          <div class="absolute inset-0 tio-place-center" aria-hidden="true">
            <span class="tio-text-lg-bold tio-fg-primary" data-test-loan-account-card-percent>
              {{this.progressPercent}}%
            </span>
            <Text @variant="muted" @size="xs">{{t "accounts.loan_summary.card.paid"}}</Text>
          </div>
        </div>
        <div class="flex-1">
          <div class="tio-text-3xl-bold tio-fg-primary mb-1" data-test-loan-account-card-balance>
            {{this.money @loan.balance 0}}
          </div>
          <div class="tio-row-extra-tight tio-fg-success mb-2">
            <PhTrendDown @size={{16}} />
            <Text @variant="inherit" @size="sm">
              {{t "accounts.loan_summary.card.monthly" amount=(this.money @loan.monthlyPayment)}}
            </Text>
          </div>
          <Text @variant="muted" @size="sm">
            {{t "accounts.loan_summary.card.interest_rate" rate=@loan.interestRate}}
          </Text>
        </div>
      </div>

      <div class="tio-row-between pt-4 border-t tio-border-subtle">
        <div class="tio-row-tight tio-fg-secondary">
          <PhCalendar @size={{18}} />
          <Text @variant="inherit" @size="sm">
            {{t "accounts.loan_summary.card.payoff" date=this.payoffMonth}}
          </Text>
        </div>
        <LinkTo
          @route="authenticated.account-activity.loans.detail"
          @model={{@loan.id}}
          class="app-text-link"
          data-test-loan-account-card-details
        >
          {{t "accounts.loan_summary.card.view_details"}}
        </LinkTo>
      </div>
    </Card>
  </template>
}
